import React from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import SEO from "../components/SEO";
import { Helmet } from "react-helmet-async";
import {
  FaSeedling,
  FaHandshake,
  FaFemale,
  FaChartLine,
  FaCertificate,
  FaAward,
  FaClipboardCheck,
  FaBullhorn,
  FaTools,
  FaGraduationCap,
  FaShoppingCart,
  FaCheckCircle,
  FaArrowRight,
} from "react-icons/fa";

const FarmerCommunity = () => {
  const { t } = useTranslation();

  // Animated fruit emojis for background
  const floatingFruits = [
    { emoji: "🌾", delay: 0, duration: 20, x: "5%", y: "10%" },
    { emoji: "🌱", delay: 2, duration: 25, x: "85%", y: "15%" },
    { emoji: "🍃", delay: 4, duration: 22, x: "10%", y: "60%" },
    { emoji: "🌾", delay: 1, duration: 23, x: "90%", y: "70%" },
    { emoji: "🌱", delay: 3, duration: 24, x: "50%", y: "5%" },
    { emoji: "🍃", delay: 5, duration: 21, x: "25%", y: "45%" },
    { emoji: "🌾", delay: 2.5, duration: 26, x: "75%", y: "50%" },
    { emoji: "🌱", delay: 4.5, duration: 19, x: "95%", y: "35%" },
  ];

  const whatWeDoItems = [
    {
      icon: <FaSeedling />,
      title: t("farmerCommunity.whatWeDo.training.title"),
      color: "from-green-500 to-green-600",
      items: [
        t("farmerCommunity.whatWeDo.training.item1"),
        t("farmerCommunity.whatWeDo.training.item2"),
        t("farmerCommunity.whatWeDo.training.item3"),
      ],
    },
    {
      icon: <FaHandshake />,
      title: t("farmerCommunity.whatWeDo.markets.title"),
      color: "from-blue-500 to-blue-600",
      items: [
        t("farmerCommunity.whatWeDo.markets.item1"),
        t("farmerCommunity.whatWeDo.markets.item2"),
      ],
    },
    {
      icon: <FaFemale />,
      title: t("farmerCommunity.whatWeDo.empowerment.title"),
      color: "from-purple-500 to-purple-600",
      items: [
        t("farmerCommunity.whatWeDo.empowerment.item1"),
        t("farmerCommunity.whatWeDo.empowerment.item2"),
      ],
    },
    {
      icon: <FaChartLine />,
      title: t("farmerCommunity.whatWeDo.traceability.title"),
      color: "from-orange-500 to-orange-600",
      items: [
        t("farmerCommunity.whatWeDo.traceability.item1"),
        t("farmerCommunity.whatWeDo.traceability.item2"),
      ],
    },
  ];

  const certifications = [
    {
      icon: <FaCertificate />,
      title: t("farmerCommunity.certifications.organic.title"),
      description: t("farmerCommunity.certifications.organic.description"),
      color: "bg-green-500",
    },
    {
      icon: <FaAward />,
      title: t("farmerCommunity.certifications.fairTrade.title"),
      description: t("farmerCommunity.certifications.fairTrade.description"),
      color: "bg-blue-500",
    },
    {
      icon: <FaClipboardCheck />,
      title: t("farmerCommunity.certifications.quality.title"),
      description: t("farmerCommunity.certifications.quality.description"),
      color: "bg-purple-500",
    },
  ];

  const communityPrograms = [
    {
      icon: <FaBullhorn />,
      title: t("farmerCommunity.programs.awareness.title"),
      description: t("farmerCommunity.programs.awareness.description"),
      color: "text-green-600",
    },
    {
      icon: <FaTools />,
      title: t("farmerCommunity.programs.equipment.title"),
      description: t("farmerCommunity.programs.equipment.description"),
      color: "text-blue-600",
    },
    {
      icon: <FaGraduationCap />,
      title: t("farmerCommunity.programs.education.title"),
      description: t("farmerCommunity.programs.education.description"),
      color: "text-purple-600",
    },
    {
      icon: <FaShoppingCart />,
      title: t("farmerCommunity.programs.market.title"),
      description: t("farmerCommunity.programs.market.description"),
      color: "text-orange-600",
    },
  ];

  const modelSteps = [
    {
      number: "01",
      title: t("farmerCommunity.model.step1.title"),
      description: t("farmerCommunity.model.step1.description"),
    },
    {
      number: "02",
      title: t("farmerCommunity.model.step2.title"),
      description: t("farmerCommunity.model.step2.description"),
    },
    {
      number: "03",
      title: t("farmerCommunity.model.step3.title"),
      description: t("farmerCommunity.model.step3.description"),
    },
    {
      number: "04",
      title: t("farmerCommunity.model.step4.title"),
      description: t("farmerCommunity.model.step4.description"),
    },
    {
      number: "05",
      title: t("farmerCommunity.model.step5.title"),
      description: t("farmerCommunity.model.step5.description"),
    },
  ];

  const impactStats = [
    {
      number: "100+",
      label: t("farmerCommunity.impact.stat1"),
    },
    {
      number: "↑",
      label: t("farmerCommunity.impact.stat2"),
    },
    {
      number: "✓",
      label: t("farmerCommunity.impact.stat3"),
    },
    {
      number: "♀",
      label: t("farmerCommunity.impact.stat4"),
    },
    {
      number: "✓",
      label: t("farmerCommunity.impact.stat5"),
    },
  ];

  return (
    <>
      {/* SEO Component */}
      <SEO
        title="Empowering Sri Lankan Organic Farmers | Sustainable Farming Partnership & Fair Trade"
        description="Supporting 100+ small-scale organic farmers in Sri Lanka with training, fair markets, and certified supply chains. Regenerative agriculture, women's empowerment, and full traceability. Partner with us for ethical, sustainable organic ingredients."
        keywords="organic farmers Sri Lanka, sustainable farming partnership, fair trade organic farming, small-scale farmers support, regenerative agriculture Sri Lanka, organic certification support, women farmers empowerment, traceable organic supply chain, ethical sourcing Sri Lanka, community-based organic farming, farmer training programs, organic farming education, soil health restoration, biodiversity protection farming, chemical-free agriculture, fair pricing farmers, guaranteed purchase organic, organic farmer cooperative, sustainable agriculture Sri Lanka, impact farming programs"
        image="https://worganaturals.com/images/community/organic-farmers-training.jpg"
        url="/farmer-community"
        type="website"
      />

      {/* Additional Structured Data */}
      <Helmet>
        {/* Organization Schema - Social Enterprise Focus */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Organization",
            name: "Worga Naturals - Farmer Community Partnership",
            alternateName: "Worga Naturals Sustainable Farming Program",
            url: "https://worganaturals.com/farmer-community",
            logo: "https://worganaturals.com/images/logo.png",
            description:
              "Empowering 100+ small-scale organic farmers across Sri Lanka through training, fair markets, and certified supply chains. Building resilient, chemical-free agriculture that delivers social and environmental impact.",
            foundingDate: "2015",
            address: {
              "@type": "PostalAddress",
              streetAddress: "No. 152/G, Meepavita, Udamapitigama",
              addressLocality: "Dompe",
              postalCode: "11680",
              addressCountry: "LK",
            },
            areaServed: {
              "@type": "Country",
              name: "Sri Lanka",
            },
            knowsAbout: [
              "Organic Farming",
              "Regenerative Agriculture",
              "Fair Trade Practices",
              "Sustainable Supply Chains",
              "Women's Empowerment",
              "Soil Health Restoration",
              "Biodiversity Protection",
              "Community Development",
            ],
            memberOf: [
              {
                "@type": "Organization",
                name: "Fair Trade International",
              },
              {
                "@type": "Organization",
                name: "Organic Farming Associations",
              },
            ],
            sameAs: [
              "https://www.facebook.com/worganaturals",
              "https://www.instagram.com/worganaturals",
              "https://www.linkedin.com/company/worganaturals",
            ],
          })}
        </script>

        {/* Course/Training Schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Course",
            name: "Organic Farming Training Program for Small-Scale Farmers",
            description:
              "Comprehensive field training covering soil health, regenerative practices, water stewardship, biodiversity protection, and organic certification pathways for Sri Lankan farmers.",
            provider: {
              "@type": "Organization",
              name: "Worga Naturals",
              sameAs: "https://worganaturals.com",
            },
            courseCode: "WORG-FARM-101",
            hasCourseInstance: {
              "@type": "CourseInstance",
              courseMode: "onsite",
              location: {
                "@type": "Place",
                address: {
                  "@type": "PostalAddress",
                  addressLocality: "Multiple Districts",
                  addressCountry: "LK",
                },
              },
              instructor: {
                "@type": "Person",
                name: "Worga Naturals Agronomy Team",
                jobTitle: "Agricultural Extension Officers",
              },
            },
            educationalLevel: "Beginner to Intermediate",
            about: [
              "Soil health and regenerative practices",
              "Composting, mulching, crop rotation",
              "Organic pest management",
              "Water stewardship and rainwater harvesting",
              "Biodiversity protection",
              "Organic certification preparation",
            ],
            teaches: [
              "Regenerative agriculture techniques",
              "Chemical-free farming methods",
              "Sustainable water management",
              "Organic certification standards",
            ],
            financialAid: {
              "@type": "MonetaryGrant",
              name: "Free Training and Equipment Support",
              description:
                "Training provided at no cost with access to equipment and tools",
            },
          })}
        </script>

        {/* Social Enterprise Schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "SocialEnterprise",
            name: "Worga Naturals Farmer Partnership Program",
            description:
              "Building resilient organic supply chains while strengthening household incomes, restoring soil health, and protecting biodiversity across Sri Lanka.",
            url: "https://worganaturals.com/farmer-community",
            missionCoverageTerritories: "Sri Lanka",
            nonprofitStatus: "Social Enterprise",
            areaServed: {
              "@type": "AdministrativeArea",
              name: "Multiple Districts in Sri Lanka",
            },
            seeks: {
              "@type": "Demand",
              name: "Partnership with Ethical Buyers",
              description:
                "Seeking partners to source traceable organic ingredients and expand programs to more farming communities",
            },
          })}
        </script>

        {/* FAQ Schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: [
              {
                "@type": "Question",
                name: "How many farmers does Worga Naturals support?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "We partner with 100+ small-scale organic farmers across multiple districts in Sri Lanka, providing training, fair markets, and certification support to build resilient, chemical-free supply chains.",
                },
              },
              {
                "@type": "Question",
                name: "What training do farmers receive?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "Farmers receive hands-on training in soil health and regenerative practices (composting, mulching, crop rotation, organic pest management), water stewardship (rainwater harvesting, efficient irrigation), and biodiversity protection (native species planting, pollinator-friendly buffers). Training is led by our agronomy team through regular workshops and field demonstrations.",
                },
              },
              {
                "@type": "Question",
                name: "How does fair pricing work?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "We provide pre-season purchase commitments with transparent price floors, timely payments, and logistics support to reduce post-harvest losses. This guaranteed market approach ensures farmers receive consistent, fair prices for their organic products.",
                },
              },
              {
                "@type": "Question",
                name: "How do you support women farmers?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "We provide targeted training, leadership development, and access to microfinance for women farmers. We also create pathways for women-led processing and value-added enterprises, with increasing numbers of women participating in leadership and enterprise roles.",
                },
              },
              {
                "@type": "Question",
                name: "What certifications do farmers receive support for?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "We support farmers in achieving organic certification (EU, USDA, JAS) and fair trade certification through training, documentation support, gap assessments, and audit readiness preparation. We also provide continuous quality support through field visits and internal inspections.",
                },
              },
              {
                "@type": "Question",
                name: "How is traceability maintained?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "Every batch is tracked from farm to finished product with batch-level traceability. We maintain community impact reporting that links customer purchases to outcomes on the ground, so buyers can see the difference their purchases make in farming communities.",
                },
              },
              {
                "@type": "Question",
                name: "What is the environmental impact?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "Our programs enhance soil organic matter, reduce chemical inputs, restore biodiversity, and protect watersheds. Farmers implement regenerative practices including composting, crop rotation, native species planting, and pollinator-friendly buffers.",
                },
              },
              {
                "@type": "Question",
                name: "How can companies partner with this program?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "Companies can partner by sourcing traceable organic ingredients from our certified farmer network. Partnerships help expand training programs, support more farming communities, and create guaranteed markets for small-scale organic farmers. Contact us to discuss partnership opportunities.",
                },
              },
            ],
          })}
        </script>

        {/* Breadcrumb Schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              {
                "@type": "ListItem",
                position: 1,
                name: "Home",
                item: "https://worganaturals.com",
              },
              {
                "@type": "ListItem",
                position: 2,
                name: "Farmer Community",
                item: "https://worganaturals.com/farmer-community",
              },
            ],
          })}
        </script>

        {/* HowTo Schema - Farmer Onboarding Process */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "HowTo",
            name: "How Farmers Join Worga Naturals Partnership Program",
            description:
              "Step-by-step process for small-scale farmers to join our organic farming partnership program",
            step: [
              {
                "@type": "HowToStep",
                position: 1,
                name: "Onboarding",
                text: "We identify farmer groups, map farms, and set customized training plans based on current practices and certification readiness.",
              },
              {
                "@type": "HowToStep",
                position: 2,
                name: "Capacity Building",
                text: "Farmers attend regular workshops and field demonstrations led by our agronomy team covering regenerative practices, soil health, and organic methods.",
              },
              {
                "@type": "HowToStep",
                position: 3,
                name: "Certification Pathway",
                text: "We conduct gap assessments, provide documentation support, and prepare farmers for organic and fair trade audits.",
              },
              {
                "@type": "HowToStep",
                position: 4,
                name: "Purchasing Framework",
                text: "Pre-agreed volumes, quality specifications, and transparent pricing are established with guaranteed purchase commitments.",
              },
              {
                "@type": "HowToStep",
                position: 5,
                name: "Monitoring & Impact",
                text: "Continuous traceability, community premium tracking, and annual impact reporting ensure accountability and continuous improvement.",
              },
            ],
            totalTime: "PT6M",
          })}
        </script>

        {/* CollectionPage Schema for Impact Statistics */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            name: "Farmer Community Impact Metrics",
            description:
              "Measurable social and environmental impact from our farmer partnership program",
            about: {
              "@type": "Thing",
              name: "Sustainable Agriculture Impact",
            },
            hasPart: [
              {
                "@type": "StatisticalPopulation",
                name: "Farmer Engagement",
                populationType: "Small-scale organic farmers",
                numConstraints: "100+",
                location: {
                  "@type": "Place",
                  name: "Multiple districts in Sri Lanka",
                },
              },
              {
                "@type": "Observation",
                name: "Income Improvement",
                description:
                  "Increased household incomes through fair pricing and guaranteed purchase plans",
                measurementTechnique:
                  "Fair trade pricing and market guarantees",
              },
              {
                "@type": "Observation",
                name: "Soil Health Enhancement",
                description:
                  "Enhanced soil organic matter and reduced chemical inputs",
                measurementTechnique: "Regenerative agriculture practices",
              },
              {
                "@type": "Observation",
                name: "Women's Empowerment",
                description:
                  "More women participating in leadership and enterprise roles",
                measurementTechnique:
                  "Targeted training and leadership development programs",
              },
              {
                "@type": "Observation",
                name: "Supply Chain Traceability",
                description:
                  "Certified organic and fair trade supply chains with batch-level traceability",
                measurementTechnique: "Blockchain and documentation systems",
              },
            ],
          })}
        </script>

        {/* Event Schema - Training Programs */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Event",
            name: "Organic Farming Field Training Workshop",
            description:
              "Hands-on training for small-scale farmers covering regenerative agriculture, soil health, and organic certification",
            eventStatus: "https://schema.org/EventScheduled",
            eventAttendanceMode:
              "https://schema.org/OfflineEventAttendanceMode",
            location: {
              "@type": "Place",
              name: "Farmer Fields - Multiple Districts",
              address: {
                "@type": "PostalAddress",
                addressCountry: "LK",
              },
            },
            organizer: {
              "@type": "Organization",
              name: "Worga Naturals",
              url: "https://worganaturals.com",
            },
            performer: {
              "@type": "Organization",
              name: "Worga Naturals Agronomy Team",
            },
            isAccessibleForFree: true,
            audience: {
              "@type": "Audience",
              audienceType: "Small-scale organic farmers",
            },
          })}
        </script>

        {/* Video Schema - Farmer Stories (if you have video) */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "VideoObject",
            name: "Empowering Sri Lankan Organic Farmers - Community Impact Stories",
            description:
              "Meet the farmers transforming their communities through organic agriculture and fair trade partnerships",
            thumbnailUrl:
              "https://worganaturals.com/images/video-thumbnails/farmer-stories.jpg",
            uploadDate: "2024-02-01",
            duration: "PT4M30S",
            contentUrl:
              "https://worganaturals.com/videos/farmer-community-impact.mp4",
          })}
        </script>

        {/* Action Schema - Partnership CTA */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Action",
            name: "Partner With Worga Naturals Farmer Program",
            description:
              "Source traceable organic ingredients and help expand programs to more farming communities",
            target: {
              "@type": "EntryPoint",
              urlTemplate: "https://worganaturals.com/contact",
              actionPlatform: [
                "http://schema.org/DesktopWebPlatform",
                "http://schema.org/MobileWebPlatform",
              ],
            },
            result: {
              "@type": "Thing",
              name: "Sustainable Supply Chain Partnership",
            },
          })}
        </script>

        {/* Place Schema - Farming Regions */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Place",
            name: "Worga Naturals Organic Farming Communities",
            description:
              "Network of 100+ small-scale organic farms across multiple districts in Sri Lanka",
            geo: {
              "@type": "GeoCoordinates",
              latitude: "7.8731",
              longitude: "80.7718",
            },
            address: {
              "@type": "PostalAddress",
              addressCountry: "LK",
            },
            containedInPlace: {
              "@type": "Country",
              name: "Sri Lanka",
            },
          })}
        </script>
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-green-50 dark:from-dark-900 dark:via-dark-800 dark:to-dark-900 relative overflow-hidden">
        {/* Animated Background Elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {floatingFruits.map((fruit, index) => (
            <motion.div
              key={index}
              className="absolute"
              style={{ left: fruit.x, top: fruit.y }}
              animate={{
                y: [0, -30, 0],
                x: [0, 15, 0],
                rotate: [0, 180, 360],
                scale: [1, 1.2, 1],
              }}
              transition={{
                duration: fruit.duration,
                repeat: Infinity,
                delay: fruit.delay,
                ease: "easeInOut",
              }}
            >
              <div className="text-5xl md:text-7xl opacity-10">
                {fruit.emoji}
              </div>
            </motion.div>
          ))}
        </div>

        {/* Brushed White Overlay */}
        <div className="absolute inset-0 pointer-events-none">
          <motion.div
            className="absolute inset-0 bg-gradient-to-br from-white/60 via-transparent to-white/40 dark:from-dark-900/60 dark:via-transparent dark:to-dark-900/40"
            animate={{
              opacity: [0.4, 0.6, 0.4],
            }}
            transition={{
              duration: 8,
              repeat: Infinity,
              ease: "easeInOut",
            }}
          />
        </div>

        {/* Main Content */}
        <div className="relative z-10">
          {/* Hero Section */}
          <section className="pt-32 pb-20 px-4">
            <div className="container mx-auto">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
                className="text-center max-w-5xl mx-auto"
              >
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 0.2, type: "spring" }}
                  className="inline-block mb-6"
                >
                  <span className="bg-gradient-to-r from-green-600 to-green-700 text-white px-6 py-2 rounded-full text-sm font-bold shadow-lg">
                    {t("farmerCommunity.hero.badge")}
                  </span>
                </motion.div>

                <h1 className="text-4xl md:text-6xl font-bold mb-6 leading-tight">
                  <span className="text-green-600 dark:text-green-500">
                    {t("farmerCommunity.hero.titlePart1")}{" "}
                  </span>
                  <span className="text-gray-900 dark:text-white">
                    {t("farmerCommunity.hero.titlePart2")}
                  </span>
                </h1>

                <p className="text-lg md:text-xl text-gray-700 dark:text-gray-300 leading-relaxed">
                  {t("farmerCommunity.hero.description")}
                </p>
              </motion.div>
            </div>
          </section>

          {/* Farm Image Section 1 */}
          <section className="py-12 px-4">
            <div className="container mx-auto">
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8 }}
                className="rounded-3xl overflow-hidden shadow-2xl"
              >
                <img
                  src="/images/farmer-community/farmland-1.webp"
                  alt="Organic Farmland"
                  className="w-full h-[400px] object-cover"
                />
              </motion.div>
            </div>
          </section>

          {/* What We Do Section */}
          <section className="py-20 px-4">
            <div className="container mx-auto">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="text-center mb-16"
              >
                <h2 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-4">
                  {t("farmerCommunity.whatWeDo.title")}
                </h2>
              </motion.div>

              <div className="grid md:grid-cols-2 gap-8">
                {whatWeDoItems.map((item, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                    className="bg-white/90 dark:bg-dark-800/90 backdrop-blur-md rounded-2xl p-8 shadow-xl hover:shadow-2xl transition-all"
                  >
                    <div
                      className={`bg-gradient-to-r ${item.color} w-16 h-16 rounded-2xl flex items-center justify-center text-white text-3xl mb-6 shadow-lg`}
                    >
                      {item.icon}
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                      {item.title}
                    </h3>
                    <ul className="space-y-3">
                      {item.items.map((listItem, idx) => (
                        <li key={idx} className="flex items-start gap-3">
                          <FaCheckCircle className="text-green-600 mt-1 flex-shrink-0" />
                          <span className="text-gray-700 dark:text-gray-300">
                            {listItem}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </motion.div>
                ))}
              </div>
            </div>
          </section>

          {/* Farm Image Section 2 */}
          <section className="py-12 px-4">
            <div className="container mx-auto">
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8 }}
                className="rounded-3xl overflow-hidden shadow-2xl"
              >
                <img
                  src="/images/farmer-community/farmland-2.webp"
                  alt="Farmer Training"
                  className="w-full h-[400px] object-cover"
                />
              </motion.div>
            </div>
          </section>

          {/* Certifications Section */}
          <section className="py-20 px-4 bg-gradient-to-br from-green-100 to-blue-50 dark:from-dark-800 dark:to-dark-900">
            <div className="container mx-auto">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="text-center mb-16"
              >
                <h2 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-4">
                  {t("farmerCommunity.certifications.title")}
                </h2>
              </motion.div>

              <div className="grid md:grid-cols-3 gap-8">
                {certifications.map((cert, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                    className="bg-white dark:bg-dark-800 rounded-2xl p-8 shadow-xl text-center"
                  >
                    <div
                      className={`${cert.color} w-20 h-20 rounded-full flex items-center justify-center text-white text-4xl mx-auto mb-6 shadow-lg`}
                    >
                      {cert.icon}
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                      {cert.title}
                    </h3>
                    <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                      {cert.description}
                    </p>
                  </motion.div>
                ))}
              </div>
            </div>
          </section>

          {/* Community Programs Section */}
          <section className="py-20 px-4">
            <div className="container mx-auto">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="text-center mb-16"
              >
                <h2 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-4">
                  {t("farmerCommunity.programs.title")}
                </h2>
              </motion.div>

              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
                {communityPrograms.map((program, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                    className="bg-white/90 dark:bg-dark-800/90 backdrop-blur-md rounded-2xl p-6 shadow-xl hover:shadow-2xl transition-all text-center"
                  >
                    <div className={`${program.color} text-5xl mb-4`}>
                      {program.icon}
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3">
                      {program.title}
                    </h3>
                    <p className="text-gray-700 dark:text-gray-300 text-sm leading-relaxed">
                      {program.description}
                    </p>
                  </motion.div>
                ))}
              </div>
            </div>
          </section>

          {/* Farm Image Section 3 */}
          <section className="py-12 px-4">
            <div className="container mx-auto">
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8 }}
                className="rounded-3xl overflow-hidden shadow-2xl"
              >
                <img
                  src="/images/farmer-community/farmland-3.webp"
                  alt="Community Development"
                  className="w-full h-[400px] object-cover"
                />
              </motion.div>
            </div>
          </section>

          {/* How It Works Section */}
          <section className="py-20 px-4 bg-gradient-to-br from-blue-50 to-green-50 dark:from-dark-900 dark:to-dark-800">
            <div className="container mx-auto">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="text-center mb-16"
              >
                <h2 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-4">
                  {t("farmerCommunity.model.title")}
                </h2>
              </motion.div>

              <div className="max-w-4xl mx-auto space-y-6">
                {modelSteps.map((step, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -30 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                    className="bg-white dark:bg-dark-800 rounded-2xl p-8 shadow-xl flex items-start gap-6"
                  >
                    <div className="bg-gradient-to-br from-green-500 to-green-600 text-white w-16 h-16 rounded-2xl flex items-center justify-center text-2xl font-bold flex-shrink-0 shadow-lg">
                      {step.number}
                    </div>
                    <div className="flex-1">
                      <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-3">
                        {step.title}
                      </h3>
                      <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                        {step.description}
                      </p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </section>

          {/* Impact at a Glance Section */}
          <section className="py-20 px-4">
            <div className="container mx-auto">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="text-center mb-16"
              >
                <h2 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-4">
                  {t("farmerCommunity.impact.title")}
                </h2>
              </motion.div>

              <div className="grid md:grid-cols-3 lg:grid-cols-5 gap-6">
                {impactStats.map((stat, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, scale: 0.8 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                    className="bg-gradient-to-br from-green-500 to-green-600 text-white rounded-2xl p-8 shadow-xl text-center"
                  >
                    <div className="text-5xl font-bold mb-4">{stat.number}</div>
                    <p className="text-sm leading-relaxed">{stat.label}</p>
                  </motion.div>
                ))}
              </div>
            </div>
          </section>

          {/* Farm Image Section 4 */}
          <section className="py-12 px-4">
            <div className="container mx-auto">
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8 }}
                className="rounded-3xl overflow-hidden shadow-2xl"
              >
                <img
                  src="/images/farmer-community/farmland-4.webp"
                  alt="Sustainable Farming"
                  className="w-full h-[400px] object-cover"
                />
              </motion.div>
            </div>
          </section>

          {/* Call to Action Section */}
          <section className="py-20 px-4">
            <div className="container mx-auto">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="bg-gradient-to-r from-green-600 to-green-700 rounded-3xl p-12 md:p-16 text-center shadow-2xl"
              >
                <h2 className="text-3xl md:text-5xl font-bold text-white mb-6">
                  {t("farmerCommunity.cta.title")}
                </h2>
                <p className="text-xl text-green-100 mb-10 max-w-3xl mx-auto leading-relaxed">
                  {t("farmerCommunity.cta.description")}
                </p>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="bg-white text-green-600 px-10 py-4 rounded-full font-bold text-lg shadow-xl hover:shadow-2xl transition-all inline-flex items-center gap-3"
                >
                  {t("farmerCommunity.cta.button")}
                  <FaArrowRight />
                </motion.button>
              </motion.div>
            </div>
          </section>
        </div>
      </div>
    </>
  );
};

export default FarmerCommunity;
